import React from "react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { Button } from "../../../components/ui/button";
import { FieldLabel, TextInput } from "../../../components/ui/FormControls";
import {
  EMPTY_NEIGHBORHOODS,
  useNeighborhoodSelector,
} from "../../../hooks/useNeighborhoodSelector";
import type { Neighborhood } from "../../../../types/neighborhood";
import type { RouteMap, WebsiteSetupForm } from "../types";

type WebsiteSetupFormFieldsProps = {
  csrfToken: string;
  routes: RouteMap;
  websiteForm?: WebsiteSetupForm;
};

type SuggestionPayload = {
  city?: string;
  neighborhoods?: Neighborhood[];
  center?: { lat: number; lng: number } | null;
  country?: string;
};

function normalizeNeighborhoods(raw: unknown): Neighborhood[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const record = entry as Record<string, unknown>;
    const name = typeof record.name === "string" ? record.name.trim() : "";
    if (!name) return [];
    const lat = typeof record.lat === "number" ? record.lat : 0;
    const lng = typeof record.lng === "number" ? record.lng : 0;
    return [{ name, lat, lng }];
  });
}

export const WebsiteSetupFormFields: React.FC<WebsiteSetupFormFieldsProps> = ({
  csrfToken,
  routes,
  websiteForm,
}) => {
  const action = routes.organizationWebsiteSetup ?? "/organization/website";
  const [serviceArea, setServiceArea] = React.useState(
    websiteForm?.serviceArea ?? "",
  );
  const [suggestions, setSuggestions] =
    React.useState<SuggestionPayload | null>(null);
  const [lookupError, setLookupError] = React.useState<string | null>(null);
  const [lookingUp, setLookingUp] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState<Neighborhood[] | null>(null);
  const lastLookup = React.useRef("");

  const neighborhoods = React.useMemo(
    () => normalizeNeighborhoods(suggestions?.neighborhoods),
    [suggestions],
  );
  const showSelector = neighborhoods.length > 0 && confirmed == null;
  const initialSelection = React.useMemo(
    () => (neighborhoods[0] ? [neighborhoods[0].name] : ("none" as const)),
    [neighborhoods],
  );

  const lookup = async (query: string) => {
    const trimmed = query.trim();
    const url = routes.organizationWebsiteServiceAreaSuggestions;
    if (!url || trimmed.length < 2 || trimmed === lastLookup.current) return;

    lastLookup.current = trimmed;
    setLookingUp(true);
    setLookupError(null);
    setConfirmed(null);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ query: trimmed }),
      });
      const data = (await response.json()) as SuggestionPayload & {
        error?: string;
      };
      if (!response.ok) {
        setSuggestions(null);
        setLookupError(data.error ?? "Could not look up nearby towns.");
        return;
      }
      setSuggestions(data);
    } catch {
      setSuggestions(null);
      setLookupError("Could not look up nearby towns.");
    } finally {
      setLookingUp(false);
    }
  };

  React.useEffect(() => {
    const trimmed = serviceArea.trim();
    if (trimmed.length < 2) return;
    const timer = window.setTimeout(() => {
      void lookup(trimmed);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [serviceArea]);

  const selector = useNeighborhoodSelector(
    showSelector ? (suggestions?.city ?? serviceArea) : "",
    showSelector ? neighborhoods : EMPTY_NEIGHBORHOODS,
    (selected) => {
      if (selected[0]?.name) {
        lastLookup.current = selected[0].name;
        setServiceArea(selected[0].name);
      }
      setConfirmed(selected);
    },
    () => {
      setConfirmed([]);
    },
    initialSelection,
    showSelector
      ? {
          placesUrl: routes.serviceAreaPlaces,
          csrfToken,
          googleMapsApiKey: undefined,
          center: suggestions?.center ?? null,
          country: suggestions?.country,
        }
      : undefined,
  );

  const storedNeighborhoods =
    confirmed && confirmed.length > 0 ? confirmed : null;

  return (
    <form action={action} method="post" className="max-w-xl">
      <AuthenticityTokenField csrfToken={csrfToken} />
      <input
        type="hidden"
        name="website[neighborhood_coordinates]"
        value={storedNeighborhoods ? JSON.stringify(storedNeighborhoods) : ""}
      />
      <div className="mb-6">
        <FieldLabel htmlFor="website_service_area">Service area</FieldLabel>
        <TextInput
          id="website_service_area"
          name="website[service_area]"
          value={serviceArea}
          placeholder="Phoenix metro"
          required
          maxLength={200}
          onChange={(event) => {
            setServiceArea(event.target.value);
            setConfirmed(null);
            lastLookup.current = "";
          }}
        />
        <p className="mt-2 text-sm text-muted-foreground">
          City or region you serve. ToolKit suggests nearby towns after you
          pause typing. Your trade and company details come from organization
          setup.
        </p>
        {lookingUp ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Looking up nearby towns…
          </p>
        ) : null}
        {lookupError ? (
          <p className="mt-2 text-sm text-destructive">{lookupError}</p>
        ) : null}
        {storedNeighborhoods ? (
          <p className="mt-2 text-sm text-foreground">
            Serving {storedNeighborhoods.map((area) => area.name).join(", ")}.
          </p>
        ) : null}
      </div>
      {showSelector ? (
        <div className="mb-6 space-y-3">
          {selector.content}
          <div className="flex items-center gap-2">{selector.bottomBar}</div>
        </div>
      ) : null}
      <Button
        type="submit"
        variant="premium"
        disabled={lookingUp || showSelector}
      >
        Create website
      </Button>
    </form>
  );
};
